import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Users, List, CheckCircle2, Pencil, Trash2, Search } from 'lucide-react';
import type { ContactBalance, Loan, LoanFormData } from '../types/loans';
import { createLoan, deleteLoan, getContactBalances, getLoans, settleLoan, updateLoan } from '../lib/services/loansService';

type View = 'list' | 'summary';

const Loans: React.FC = () => {
  const [view, setView] = useState<View>('list');
  const [loans, setLoans] = useState<Loan[]>([]);
  const [contacts, setContacts] = useState<ContactBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSettled, setShowSettled] = useState(false);
  const [search, setSearch] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState<Loan | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<LoanFormData>(() => ({
    contact_name: '',
    amount: 0,
    description: '',
    date: new Date().toISOString().slice(0, 10),
    is_lending: true,
  }));

  const filteredLoans = useMemo(() => loans, [loans]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [loansData, contactsData] = await Promise.all([
        getLoans({ showSettled, search }),
        getContactBalances(),
      ]);
      setLoans(loansData);
      setContacts(contactsData);
    } catch (e) {
      console.error(e);
      alert("Impossible de charger les prêts. Vérifiez votre connexion.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSettled]);

  const openCreate = () => {
    setEditingLoan(null);
    setForm({
      contact_name: '',
      amount: 0,
      description: '',
      date: new Date().toISOString().slice(0, 10),
      is_lending: true,
    });
    setIsModalOpen(true);
  };

  const openEdit = (loan: Loan) => {
    setEditingLoan(loan);
    setForm({
      contact_name: loan.contact_name,
      amount: loan.amount,
      description: loan.description ?? '',
      date: loan.date,
      is_lending: loan.is_lending,
    });
    setIsModalOpen(true);
  };

  const onSave = async () => {
    if (!form.contact_name.trim() || !form.amount || form.amount <= 0) {
      alert('Veuillez renseigner un nom et un montant > 0.');
      return;
    }

    setSaving(true);
    try {
      if (editingLoan) {
        await updateLoan(editingLoan.id, form);
      } else {
        await createLoan(form);
      }
      setIsModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert("Impossible d'enregistrer le prêt.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (loanId: string) => {
    if (!window.confirm('Supprimer ce prêt ?')) return;
    try {
      await deleteLoan(loanId);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Suppression impossible.');
    }
  };

  const onSettle = async (loanId: string) => {
    try {
      await settleLoan(loanId);
      await loadData();
    } catch (e) {
      console.error(e);
      alert("Impossible de marquer comme remboursé.");
    }
  };

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);

  const formatBalanceText = (b: ContactBalance) => {
    if (b.balance > 0) return `On me doit ${formatAmount(b.balance)}`;
    if (b.balance < 0) return `Je dois ${formatAmount(Math.abs(b.balance))}`;
    return 'Quitte';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-h1 text-white">Prêts entre amis</h1>
          <p className="text-body-sm text-dark-400 mt-1">
            Suivez ce que vous prêtez et empruntez à vos proches.
          </p>
        </div>

        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nouveau prêt
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 bg-dark-900 border border-dark-800 rounded-lg px-3 py-2 flex-1">
          <Search className="w-4 h-4 text-dark-400" />
          <input
            className="bg-transparent outline-none text-white w-full placeholder:text-dark-500"
            placeholder="Rechercher par nom ou description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') loadData();
            }}
          />
          <button
            className="text-dark-300 hover:text-white text-sm"
            onClick={() => loadData()}
            title="Rechercher"
          >
            OK
          </button>
        </div>

        <label className="flex items-center gap-2 text-dark-200 text-sm select-none">
          <input
            type="checkbox"
            checked={showSettled}
            onChange={() => setShowSettled((v) => !v)}
            className="rounded"
          />
          Afficher terminés
        </label>

        <div className="flex gap-2">
          <button
            onClick={() => setView('list')}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              view === 'list'
                ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                : 'bg-dark-900 border-dark-800 text-dark-200 hover:text-white'
            }`}
          >
            <List className="w-4 h-4" />
            Liste
          </button>
          <button
            onClick={() => setView('summary')}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              view === 'summary'
                ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                : 'bg-dark-900 border-dark-800 text-dark-200 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4" />
            Synthèse
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-dark-300">Chargement...</p>
          </div>
        </div>
      ) : view === 'summary' ? (
        contacts.length === 0 ? (
          <div className="text-center py-16 text-dark-300">Aucun contact trouvé.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {contacts.map((c) => (
              <div key={c.contact_name} className="bg-dark-900 border border-dark-800 rounded-lg p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold text-lg truncate">{c.contact_name}</h3>
                    <p className="text-dark-400 text-sm mt-1">
                      {c.transaction_count} opération{c.transaction_count > 1 ? 's' : ''}
                    </p>
                  </div>
                  <div
                    className={`text-xl font-bold ${
                      c.balance > 0 ? 'text-green-400' : c.balance < 0 ? 'text-red-400' : 'text-dark-300'
                    }`}
                  >
                    {c.balance > 0 ? '+' : ''}
                    {formatAmount(c.balance)}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-dark-950 rounded-lg p-3 border border-dark-800">
                    <p className="text-dark-400">Prêté</p>
                    <p className="text-green-400 font-semibold">{formatAmount(c.total_lent)}</p>
                  </div>
                  <div className="bg-dark-950 rounded-lg p-3 border border-dark-800">
                    <p className="text-dark-400">Emprunté</p>
                    <p className="text-red-400 font-semibold">{formatAmount(c.total_borrowed)}</p>
                  </div>
                </div>

                <div className="mt-4 text-sm text-dark-200">{formatBalanceText(c)}</div>
              </div>
            ))}
          </div>
        )
      ) : filteredLoans.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-dark-300">Aucun prêt trouvé.</p>
          <button
            onClick={openCreate}
            className="mt-4 inline-flex items-center gap-2 bg-dark-800 hover:bg-dark-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Créer un prêt
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLoans.map((loan) => (
            <div key={loan.id} className="bg-dark-900 border border-dark-800 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold truncate">{loan.contact_name}</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        loan.is_settled ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
                      }`}
                    >
                      {loan.is_settled ? 'Terminé' : 'En cours'}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-dark-400 flex flex-wrap gap-x-3 gap-y-1">
                    <span className={loan.is_lending ? 'text-green-400' : 'text-red-400'}>
                      {loan.is_lending ? '+' : '-'}
                      {formatAmount(loan.amount)}
                    </span>
                    <span>•</span>
                    <span>{new Date(loan.date).toLocaleDateString('fr-FR')}</span>
                    <span>•</span>
                    <span className={loan.is_lending ? 'text-green-400' : 'text-red-400'}>
                      {loan.is_lending ? 'Je prête' : "J'emprunte"}
                    </span>
                  </div>
                  {loan.description ? (
                    <p className="mt-2 text-sm text-dark-300 break-words">{loan.description}</p>
                  ) : null}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {!loan.is_settled && (
                    <>
                      <button
                        onClick={() => openEdit(loan)}
                        className="p-2 rounded-lg hover:bg-dark-800 text-dark-200 hover:text-white transition-colors"
                        title="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onSettle(loan.id)}
                        className="p-2 rounded-lg hover:bg-dark-800 text-dark-200 hover:text-white transition-colors"
                        title="Marquer comme remboursé"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => onDelete(loan.id)}
                    className="p-2 rounded-lg hover:bg-dark-800 text-red-300 hover:text-red-200 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal create/edit */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="w-full max-w-lg bg-dark-900 border border-dark-800 rounded-xl shadow-xl">
            <div className="px-5 py-4 border-b border-dark-800 flex items-center justify-between">
              <h2 className="text-white font-semibold">
                {editingLoan ? 'Modifier le prêt' : 'Nouveau prêt'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-dark-300 hover:text-white"
                title="Fermer"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="space-y-1">
                <label className="text-sm text-dark-200">Ami *</label>
                <input
                  className="w-full bg-dark-950 border border-dark-800 rounded-lg px-3 py-2 text-white outline-none"
                  value={form.contact_name}
                  onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))}
                  placeholder="Nom de l'ami"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-dark-200">Montant (€) *</label>
                <input
                  className="w-full bg-dark-950 border border-dark-800 rounded-lg px-3 py-2 text-white outline-none"
                  type="number"
                  step="0.01"
                  min="0"
                  value={String(form.amount ?? 0)}
                  onChange={(e) => setForm((p) => ({ ...p, amount: Number(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm text-dark-200">Date</label>
                  <input
                    className="w-full bg-dark-950 border border-dark-800 rounded-lg px-3 py-2 text-white outline-none"
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm text-dark-200">Sens du prêt</label>
                  <select
                    className="w-full bg-dark-950 border border-dark-800 rounded-lg px-3 py-2 text-white outline-none"
                    value={form.is_lending ? 'lend' : 'borrow'}
                    onChange={(e) => setForm((p) => ({ ...p, is_lending: e.target.value === 'lend' }))}
                  >
                    <option value="lend">Je prête</option>
                    <option value="borrow">J&apos;emprunte</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm text-dark-200">Description (optionnel)</label>
                <textarea
                  className="w-full bg-dark-950 border border-dark-800 rounded-lg px-3 py-2 text-white outline-none min-h-[90px]"
                  value={form.description ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Détails du prêt..."
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-dark-800 flex justify-end gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-dark-800 hover:bg-dark-700 text-white transition-colors"
                disabled={saving}
              >
                Annuler
              </button>
              <button
                onClick={onSave}
                className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white transition-colors disabled:opacity-60"
                disabled={saving}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Loans;

